import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api } from "../api";
import { obligations as plural } from "../format";
import { navigate } from "../router";
import type {
  Activity, ActivityGap, Assessment, Contact, Obligation, Question, Verdicts,
} from "../types";

/**
 * Data-protection compliance, for a business owner rather than a lawyer.
 * Three tabs, each a URL, in the order the work happens: answer the
 * profile, read what you owe, keep the art. 30 register.
 *
 * THE THINGS THIS INTERFACE REFUSES TO DO
 *
 * No score, no percentage, no green tick that means "compliant". The step
 * counts are how far through the questions the customer is, and say so.
 *
 * Nothing unanswered is shown as "no". A card with no answer has a dashed
 * border and says what it could bring; an obligation that turns on it sits
 * in "Nedecise" with the question named, never in "Nu se aplică".
 *
 * Every obligation carries its article, visible, next to what it asks for.
 *
 * The server is the source of truth for all of it: what applies, what an
 * answer would add (Question.impact), and the three verdicts. This file
 * only arranges them.
 */

export type ComplianceTab = "profile" | "obligations" | "register";

type Answer = boolean | number | string | null;

const STATE_LABEL: Record<string, string> = {
  open: "de făcut",
  in_progress: "în lucru",
  done: "rezolvat",
  not_applicable: "nu se aplică",
};

const FINE_LABEL: Record<string, string> = {
  "1m-1pct": "până la 1 000 000 lei sau 1% din cifra de afaceri",
  "2m-2pct": "până la 2 000 000 lei sau 2% din cifra de afaceri",
};

const ROLE_LABEL: Record<Contact["role"], string> = {
  operator: "Operator (firma dumneavoastră)",
  operator_asociat: "Operator asociat",
  reprezentant: "Reprezentant în Republica Moldova",
  dpo: "Responsabil cu protecția datelor",
};

/** Short names for the "Răspundeți la:" chips. */
const FIELD_LABEL: Record<string, string> = {
  legal_form: "forma juridică", employee_count: "câți angajați",
  is_public_authority: "autoritate publică", established_in_md: "sediul în RM",
  has_employees: "angajați", processing_occasional: "lucru zilnic cu date",
  uses_processors: "cine vă vede datele", acts_as_processor: "date ale altor firme",
  joint_controllers: "decizii cu un partener", relies_on_consent: "acordul persoanei",
  collects_from_third_parties: "date din alte surse", processes_idnp: "IDNP",
  idnp_basis_legitimate_interest: "scopul IDNP",
  processes_special_categories: "sănătate / amprente",
  processes_criminal_data: "cazier", uses_cctv: "camere",
  cctv_public_area: "ce filmează camerele", does_profiling: "clasificare automată",
  automated_decisions: "decizii automate",
  large_scale_monitoring: "urmărirea ca activitate",
  sends_data_abroad: "servicii din străinătate", has_dpo: "responsabil DPO",
  sector: "domeniul",
};

/**
 * The profile in four steps. Questions keep the catalogue's reviewed
 * wording; only two cards are the design's own, because they are not the
 * catalogue's questions any more: the staff-size buttons (employee_count
 * and has_employees at once) and the inverted daily-processing card.
 */
interface Card {
  field: string;
  /** Da stores false: "Lucrați zilnic" is processing_occasional = false. */
  invert?: { title: string; help: string };
  follow?: string;
}

const STEPS: { id: string; title: string; sub: string; firma?: true; cards?: Card[] }[] = [
  { id: "firma", title: "Firma", sub: "Patru lucruri de bază.", firma: true },
  {
    id: "oameni", title: "Oameni și parteneri", sub: "Cine lucrează cu datele și cât de des.",
    cards: [
      {
        field: "processing_occasional",
        invert: {
          title: "Lucrați zilnic cu date despre oameni",
          help: "Salarii, bază de clienți, magazin online. Dacă da, scutirea de evidență de la art. 30 alin. (5) nu se aplică.",
        },
      },
      { field: "uses_processors" },
      { field: "acts_as_processor" },
      { field: "joint_controllers" },
    ],
  },
  {
    id: "date", title: "Ce adunați", sub: "Documentele din dosare contează.",
    cards: [
      { field: "processes_idnp", follow: "idnp_basis_legitimate_interest" },
      { field: "relies_on_consent" },
      { field: "collects_from_third_parties" },
      { field: "processes_special_categories" },
      { field: "processes_criminal_data" },
    ],
  },
  {
    id: "unelte", title: "Unelte", sub: "Programe și aparate care adună date.",
    cards: [
      { field: "sends_data_abroad" },
      { field: "uses_cctv", follow: "cctv_public_area" },
      { field: "does_profiling" },
      { field: "automated_decisions" },
      { field: "large_scale_monitoring" },
      { field: "has_dpo" },
    ],
  },
];

const FIRMA_FIELDS = ["legal_form", "employee_count", "established_in_md", "sector"];

/** Which step holds a field, for the "Răspundeți la:" jump. */
const FIELD_STEP: Record<string, number> = { is_public_authority: 0, has_employees: 0 };
STEPS.forEach((s, i) => {
  if (s.firma) FIRMA_FIELDS.forEach((f) => (FIELD_STEP[f] = i));
  s.cards?.forEach((c) => {
    FIELD_STEP[c.field] = i;
    if (c.follow) FIELD_STEP[c.follow] = i;
  });
});

/**
 * Representative counts for the staff-size buttons. Only the 250 threshold
 * is read by any rule (art. 30 alin. (5)); the band is what was said.
 */
const BUCKETS = [
  { label: "Doar eu", floor: 0, count: 0, employees: false },
  { label: "1–9", floor: 1, count: 5, employees: true },
  { label: "10–49", floor: 10, count: 25, employees: true },
  { label: "50–249", floor: 50, count: 120, employees: true },
  { label: "250+", floor: 250, count: 250, employees: true },
];

/** The band a stored count falls in - by the band's floor, not its
 *  representative count, so a typed 12 reads as 10–49. */
function bucketOf(count: Answer): string | null {
  if (typeof count !== "number") return null;
  let label: string | null = null;
  for (const b of BUCKETS) if (count >= b.floor) label = b.label;
  return label;
}

const SHORT_FORM: Record<string, string> = { "Întreprindere individuală": "Î.I." };

/** legis.md, so the customer can read the article we are citing. */
function articleUrl(familyId: string, path: string): string {
  return `https://www.legis.md/cautare/getResults?doc_id=${familyId}#${path}`;
}

interface Change {
  label: string;
  added: string[];
  removed: string[];
  codes: string[];
}

// --------------------------------------------------------------- the section

export function Compliance({ tab }: { tab: ComplianceTab }) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [gaps, setGaps] = useState<ActivityGap[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [step, setStep] = useState(0);
  const [focus, setFocus] = useState<string | null>(null);
  const [change, setChange] = useState<Change | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reloadRegister = useCallback(async () => {
    const [r, g] = await Promise.all([api.register(), api.registerGaps()]);
    setActivities(r);
    setGaps(g);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [q, a, c] = await Promise.all([
          api.complianceQuestions(), api.assessment(), api.contacts(),
        ]);
        setQuestions(q);
        setAssessment(a);
        setContacts(c);
        await reloadRegister();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Eroare");
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadRegister]);

  // Ring the card a "Răspundeți la:" chip pointed at, then let it go.
  useEffect(() => {
    if (!focus) return;
    const el = document.getElementById(`t-${focus}`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(() => setFocus(null), 2400);
    return () => clearTimeout(t);
  }, [focus, step, tab]);

  async function save(patch: Record<string, Answer>, label: string) {
    const key = Object.keys(patch)[0];
    setBusy(key);
    const before = new Set(assessment?.owed.map((o) => o.code) ?? []);
    const titles = new Map(
      [...(assessment?.owed ?? []), ...(assessment?.unknown ?? []), ...(assessment?.exempt ?? [])]
        .map((o) => [o.code, o.title]),
    );
    try {
      // One answer can bring a whole obligation into scope and add a
      // suggested register row, so both are refetched rather than patched.
      const next = await api.saveProfile(patch);
      setAssessment(next);
      const after = new Set(next.owed.map((o) => o.code));
      setChange({
        label,
        added: next.owed.filter((o) => !before.has(o.code)).map((o) => o.title),
        removed: [...before].filter((c) => !after.has(c)).map((c) => titles.get(c) ?? c),
        codes: next.owed.filter((o) => !before.has(o.code)).map((o) => o.code),
      });
      const [q] = await Promise.all([api.complianceQuestions(), reloadRegister()]);
      setQuestions(q);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    } finally {
      setBusy(null);
    }
  }

  async function patch(code: string, body: { state?: string; evidence_ref?: string; determination?: string }) {
    setBusy(code);
    try {
      setAssessment(await api.setObligation(code, body));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    } finally {
      setBusy(null);
    }
  }

  async function registerWork(id: string, work: () => Promise<unknown>) {
    setBusy(id);
    try {
      await work();
      await reloadRegister();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare");
    } finally {
      setBusy(null);
    }
  }

  function jump(field: string) {
    setStep(FIELD_STEP[field] ?? 0);
    setFocus(field);
    navigate("/compliance");
  }

  if (loading) {
    return (
      <div className="page"><div className="page-body"><p className="muted">se încarcă…</p></div></div>
    );
  }

  const answers: Record<string, Answer> = Object.fromEntries(questions.map((q) => [q.field, q.answer]));
  const byField = new Map(questions.map((q) => [q.field, q]));
  const has = (f: string) => answers[f] !== null && answers[f] !== undefined;

  /** The fields a step asks, as visible now - follow-ups only once opened. */
  function stepFields(i: number): string[] {
    const s = STEPS[i];
    if (s.firma) return FIRMA_FIELDS.filter((f) => byField.has(f));
    const out: string[] = [];
    for (const c of s.cards ?? []) {
      if (!byField.has(c.field)) continue;
      out.push(c.field);
      if (c.follow && byField.has(c.follow)) out.push(c.follow);
    }
    return out;
  }
  const progress = STEPS.map((_, i) => {
    const fs = stepFields(i);
    return { done: fs.filter(has).length, total: fs.length };
  });
  const fullSteps = progress.filter((p) => p.total > 0 && p.done === p.total).length;
  const unconfirmed = activities.filter((a) => !a.confirmed).length;

  const tabs: { id: ComplianceTab; label: string; n: ReactNode; to: string }[] = [
    { id: "profile", label: "Profil", n: `${fullSteps}/${STEPS.length}`, to: "/compliance" },
    { id: "obligations", label: "Obligații", n: assessment?.owed.length ?? 0, to: "/compliance/obligations" },
    {
      id: "register", label: "Registru",
      n: unconfirmed ? `${unconfirmed} de confirmat` : activities.length,
      to: "/compliance/register",
    },
  ];

  return (
    <div className="page compliance">
      <div className="page-top">
        <div className="page-top-row">
          <h1>Conformitate</h1>
          <span className="law">Legea 195/2024 privind protecția datelor</span>
          {assessment && !assessment.reviewed_by_lawyer && (
            // The gate is in the data: obligation.reviewed_at. Until every
            // live entry is signed off, the screen says so.
            <span className="draftnote" title="Conținutul juridic al acestui modul nu a fost încă verificat de un avocat specializat în protecția datelor. Folosiți-l ca punct de plecare, nu ca opinie juridică.">
              Neverificat încă de un avocat
            </span>
          )}
        </div>
        <div className="ttabs" role="tablist" aria-label="Secțiuni">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? "on" : undefined}
              onClick={() => navigate(t.to)}
            >
              {t.label} <span className="cnt">{t.n}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Keyed on the tab: each tab opens at its top, not at the scroll
          position the previous one was left at. */}
      <div className="page-body" key={tab}>
        {error && <p className="error" role="alert">{error}</p>}

        {tab === "profile" && assessment && (
          <Profile
            step={step}
            setStep={setStep}
            progress={progress}
            questions={byField}
            answers={answers}
            focus={focus}
            busy={busy}
            change={change}
            assessment={assessment}
            onSave={save}
          />
        )}

        {tab === "obligations" && assessment && (
          <Duties
            assessment={assessment}
            answers={answers}
            fresh={change?.codes ?? []}
            busy={busy}
            onPatch={patch}
            onJump={jump}
          />
        )}

        {tab === "register" && (
          <Register
            activities={activities}
            gaps={gaps}
            contacts={contacts}
            busy={busy}
            onSave={(id, body) => void registerWork(id, () => api.updateActivity(id, body))}
            onDelete={(id) => void registerWork(id, () => api.deleteActivity(id))}
            onAdd={() =>
              void registerWork("new", () =>
                api.createActivity({ ...EMPTY_ACTIVITY, name: "Activitate nouă" }),
              )
            }
            onSaveContact={async (body) => {
              setBusy(body.role);
              try {
                setContacts(await api.saveContact(body));
              } catch (e) {
                setError(e instanceof Error ? e.message : "Eroare");
              } finally {
                setBusy(null);
              }
            }}
            onError={setError}
          />
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- profile

function Profile({
  step, setStep, progress, questions, answers, focus, busy, change, assessment, onSave,
}: {
  step: number;
  setStep: (i: number) => void;
  progress: { done: number; total: number }[];
  questions: Map<string, Question>;
  answers: Record<string, Answer>;
  focus: string | null;
  busy: string | null;
  change: Change | null;
  assessment: Assessment;
  onSave: (patch: Record<string, Answer>, label: string) => void;
}) {
  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <div className="profile">
      <div className="steps" role="tablist" aria-label="Pași">
        {STEPS.map((st, i) => {
          const p = progress[i];
          const cls = ["step", i === step ? "on" : "", p.total > 0 && p.done === p.total ? "full" : ""]
            .filter(Boolean).join(" ");
          return (
            <button key={st.id} className={cls} role="tab" aria-selected={i === step} onClick={() => setStep(i)}>
              <span className="sn">{i + 1}</span>
              <span className="sl">
                <strong>{st.title}</strong>
                <span>{p.done} din {p.total}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="stage">
        <div className="stage-head">
          <h2>{s.title}</h2>
          <p>{s.sub}</p>
        </div>

        <div className="tiles">
          {s.firma ? (
            <FirmaTiles questions={questions} answers={answers} focus={focus} busy={busy} onSave={onSave} />
          ) : (
            (s.cards ?? [])
              .filter((c) => questions.has(c.field))
              .map((c) => (
                <QuestionCard
                  key={c.field}
                  card={c}
                  q={questions.get(c.field)!}
                  follow={c.follow ? questions.get(c.follow) : undefined}
                  focus={focus}
                  busy={busy}
                  onSave={onSave}
                />
              ))
          )}
        </div>

        <div className="stage-foot">
          <button className="btn" disabled={step === 0} onClick={() => setStep(Math.max(0, step - 1))}>
            Înapoi
          </button>
          <button
            className="btn primary"
            onClick={() => (last ? navigate("/compliance/obligations") : setStep(step + 1))}
          >
            {last ? "Vedeți obligațiile" : "Continuă"}
          </button>
        </div>
      </div>

      <aside className="live" aria-live="polite">
        <h3>Efectul răspunsurilor</h3>
        <div className="nums">
          <div className="num owed"><b>{assessment.owed.length}</b><span>de făcut</span></div>
          <div className="num"><b>{assessment.unknown.length}</b><span>nedecise</span></div>
          <div className="num"><b>{assessment.exempt.length}</b><span>nu se aplică</span></div>
        </div>
        <div className={change ? "change" : "change idle"}>
          <strong>{change ? change.label : "Răspundeți — aici vedeți efectul."}</strong>
          {change?.added.length ? <span className="add">+ {change.added.join(" · ")}</span> : null}
          {change?.removed.length ? <span className="rem">− {change.removed.join(" · ")}</span> : null}
          {change && !change.added.length && !change.removed.length && (
            <span className="rem">Lista de obligații nu s-a schimbat.</span>
          )}
        </div>
        <button className="linkbtn" onClick={() => navigate("/compliance/obligations")}>
          Vedeți obligațiile →
        </button>
      </aside>
    </div>
  );
}

function effectLabel(q: Question, shown: boolean | null, yesMeans: "yes" | "no"): string {
  const impact = q.impact;
  if (!impact) return "";
  if (shown === null) {
    const n = impact[yesMeans];
    return n > 0 ? `poate aduce ${plural(n)}` : "nu schimbă lista";
  }
  if (impact.adds > 0) return `+ ${plural(impact.adds)}`;
  return shown ? "nimic nou" : "nu aduce nimic";
}

function YesNo({
  value, onPick, disabled, labelledBy,
}: {
  value: boolean | null;
  onPick: (v: boolean | null) => void;
  disabled: boolean;
  labelledBy: string;
}) {
  return (
    <div className="yn2" role="group" aria-labelledby={labelledBy}>
      {[true, false].map((v) => (
        <button
          key={String(v)}
          className={value === v ? (v ? "y" : "n") : undefined}
          aria-pressed={value === v}
          disabled={disabled}
          // Clicking the chosen answer again clears it: "I answered that
          // wrongly" without having to pick the other one.
          onClick={() => onPick(value === v ? null : v)}
        >
          {v ? "Da" : "Nu"}
        </button>
      ))}
    </div>
  );
}

function QuestionCard({
  card, q, follow, focus, busy, onSave,
}: {
  card: Card;
  q: Question;
  follow?: Question;
  focus: string | null;
  busy: string | null;
  onSave: (patch: Record<string, Answer>, label: string) => void;
}) {
  const raw = q.answer as boolean | null;
  // The inverted card shows Da for processing_occasional = false.
  const shown = raw === null ? null : card.invert ? !raw : raw;
  const title = card.invert?.title ?? q.question;
  const help = card.invert?.help ?? q.help;
  const cls = ["tile", shown === true ? "yes" : shown === false ? "no" : "",
    focus === q.field || (follow && focus === follow.field) ? "focus" : ""].filter(Boolean).join(" ");

  function pick(v: boolean | null) {
    const stored = v === null ? null : card.invert ? !v : v;
    // "Altcineva vă vede datele?: Da" reads badly; the question mark goes.
    onSave({ [q.field]: stored }, `${title.replace(/\?$/, "")}: ${v === null ? "fără răspuns" : v ? "Da" : "Nu"}`);
  }

  return (
    <div id={`t-${q.field}`} className={cls}>
      <span className="effect">{effectLabel(q, shown, card.invert ? "no" : "yes")}</span>
      <h3 id={`h-${q.field}`}>{title}</h3>
      {help && <p className="help">{help}</p>}
      <YesNo value={shown} onPick={pick} disabled={busy === q.field} labelledBy={`h-${q.field}`} />
      {follow && shown === true && (
        <div className="follow" id={`t-${follow.field}`}>
          <span id={`h-${follow.field}`}>{follow.question}</span>
          {follow.help && <span className="help">{follow.help}</span>}
          <YesNo
            value={follow.answer as boolean | null}
            disabled={busy === follow.field}
            labelledBy={`h-${follow.field}`}
            onPick={(v) =>
              onSave({ [follow.field]: v }, `${FIELD_LABEL[follow.field] ?? follow.field}: ${v === null ? "fără răspuns" : v ? "Da" : "Nu"}`)
            }
          />
        </div>
      )}
    </div>
  );
}

function FirmaTiles({
  questions, answers, focus, busy, onSave,
}: {
  questions: Map<string, Question>;
  answers: Record<string, Answer>;
  focus: string | null;
  busy: string | null;
  onSave: (patch: Record<string, Answer>, label: string) => void;
}) {
  const form = questions.get("legal_form");
  const forms = form?.options.length ? form.options : ["SRL", "SA", "Întreprindere individuală", "ONG", "Autoritate publică", "Alta"];
  const bucket = bucketOf(answers.employee_count);
  const md = answers.established_in_md as boolean | null;
  const [sector, setSector] = useState(String(answers.sector ?? ""));
  useEffect(() => setSector(String(answers.sector ?? "")), [answers.sector]);
  const ring = (f: string) => (focus === f || (f === "legal_form" && focus === "is_public_authority") ||
    (f === "employee_count" && focus === "has_employees") ? " focus" : "");

  return (
    <>
      <div id="t-legal_form" className={`tile${answers.legal_form ? " yes" : ""}${ring("legal_form")}`}>
        <h3 id="h-legal_form">Forma juridică</h3>
        <div className="pills" role="group" aria-labelledby="h-legal_form">
          {forms.map((f) => {
            const on = answers.legal_form === f;
            return (
              <button
                key={f}
                className={on ? "pill on" : "pill"}
                aria-pressed={on}
                disabled={busy === "legal_form"}
                title={SHORT_FORM[f] ? f : undefined}
                // "Autoritate publică" is a form and also the art. 37(1)(a)
                // answer; one button sets both, so they cannot disagree.
                onClick={() =>
                  onSave(
                    on
                      ? { legal_form: null, is_public_authority: null }
                      : { legal_form: f, is_public_authority: f === "Autoritate publică" },
                    `Forma juridică: ${on ? "fără răspuns" : f}`,
                  )
                }
              >
                {SHORT_FORM[f] ?? f}
              </button>
            );
          })}
        </div>
      </div>

      <div id="t-employee_count" className={`tile${bucket ? " yes" : ""}${ring("employee_count")}`}>
        <h3 id="h-employee_count">Câți oameni lucrează la dvs.</h3>
        <div className="pills" role="group" aria-labelledby="h-employee_count">
          {BUCKETS.map((b) => {
            const on = bucket === b.label;
            return (
              <button
                key={b.label}
                className={on ? "pill on" : "pill"}
                aria-pressed={on}
                disabled={busy === "employee_count"}
                onClick={() =>
                  onSave(
                    on
                      ? { employee_count: null, has_employees: null }
                      : { employee_count: b.count, has_employees: b.employees },
                    `Angajați: ${on ? "fără răspuns" : b.label}`,
                  )
                }
              >
                {b.label}
              </button>
            );
          })}
        </div>
      </div>

      <div id="t-established_in_md" className={`tile${md !== null ? " yes" : ""}${ring("established_in_md")}`}>
        <h3 id="h-established_in_md">Sediu în Republica Moldova</h3>
        <YesNo
          value={md}
          disabled={busy === "established_in_md"}
          labelledBy="h-established_in_md"
          onPick={(v) => onSave({ established_in_md: v }, `Sediu în RM: ${v === null ? "fără răspuns" : v ? "Da" : "Nu"}`)}
        />
      </div>

      <div id="t-sector" className={`tile${answers.sector ? " yes" : ""}${ring("sector")}`}>
        <label htmlFor="sector"><h3>Domeniul</h3></label>
        <input
          id="sector"
          type="text"
          placeholder="Comerț, IT, medicină…"
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          onBlur={() =>
            sector.trim() !== String(answers.sector ?? "") &&
            onSave({ sector: sector.trim() || null }, `Domeniul: ${sector.trim() || "fără răspuns"}`)
          }
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------- obligations

const RECORD_REASON: Record<string, string> = {
  processing_occasional: "Lucrați zilnic cu date — asta singur anulează scutirea.",
  employee_count: "Aveți 250 de angajați sau mai mulți.",
  processes_special_categories: "Prelucrați date sensibile.",
  processes_criminal_data: "Prelucrați date despre condamnări.",
};

const DPO_REASON: Record<string, string> = {
  is_public_authority: "Sunteți autoritate publică (art. 37 alin. (1) lit. a)).",
  large_scale_monitoring: "Urmărirea sistematică a persoanelor e activitatea principală (lit. b)).",
};

function verdictCards(v: Verdicts, answers: Record<string, Answer>) {
  const out: { cls: string; art: string; head: string; body: string; ask?: string }[] = [];

  if (v.records_exempt === false) {
    out.push({
      cls: "must", art: "Evidența · art. 30 alin. (5)", head: "Nu sunteți scutit de evidență",
      body: v.records_triggers.map((f) => RECORD_REASON[f]).filter(Boolean).join(" "),
    });
  } else if (v.records_exempt === true) {
    out.push({
      cls: "free", art: "Evidența · art. 30 alin. (5)", head: "Scutit de evidență, pe răspunsurile date",
      body: "Sub 250 de angajați, prelucrare ocazională, fără date sensibile sau penale. Consemnați concluzia: dacă prelucrarea poate genera un risc, scutirea cade.",
    });
  } else {
    out.push({ cls: "wait", art: "Evidența · art. 30", head: "Încă nu știm", body: "Răspundeți în profil.", ask: "processing_occasional" });
  }

  if (v.dpo_required === true) {
    out.push({
      cls: "must", art: "DPO · art. 37", head: "Vă trebuie un responsabil DPO",
      body: v.dpo_triggers.map((f) => DPO_REASON[f]).filter(Boolean).join(" "),
    });
  } else if (v.dpo_required === false) {
    out.push({
      cls: "free", art: "DPO · art. 37", head: "Nu vă trebuie un DPO",
      body: "Niciunul din cele trei cazuri de la art. 37 alin. (1) nu vă privește. Desemnarea rămâne opțională.",
    });
  } else if (answers.processes_special_categories === true || answers.processes_criminal_data === true) {
    // Limb c) turns on "pe scară largă", which no checkbox settles.
    out.push({
      cls: "wait", art: "DPO · art. 37 alin. (1) lit. c)", head: "Depinde de scară",
      body: "Prelucrați date sensibile; DPO e obligatoriu doar dacă e activitatea principală, pe scară largă. Consemnați concluzia în obligația de mai jos.",
    });
  } else {
    out.push({ cls: "wait", art: "DPO · art. 37", head: "Încă nu știm", body: "Răspundeți în profil.", ask: "large_scale_monitoring" });
  }

  if (v.consent_reuse === true) {
    out.push({
      cls: "free", art: "Acord · art. 90 alin. (8)", head: "Nu cereți din nou acordul",
      body: "Acordul dat sub Legea 133/2011 rămâne valabil, dacă îndeplinea deja condițiile art. 7.",
    });
  }
  return out;
}

function Duties({
  assessment, answers, fresh, busy, onPatch, onJump,
}: {
  assessment: Assessment;
  answers: Record<string, Answer>;
  fresh: string[];
  busy: string | null;
  onPatch: (code: string, body: { state?: string; evidence_ref?: string; determination?: string }) => void;
  onJump: (field: string) => void;
}) {
  const [filter, setFilter] = useState<"owed" | "unknown" | "exempt">("owed");
  const [open, setOpen] = useState<string | null>(null);
  const lists = { owed: assessment.owed, unknown: assessment.unknown, exempt: assessment.exempt };
  const cards = assessment.verdicts ? verdictCards(assessment.verdicts, answers) : [];

  return (
    <>
      <div className="verdicts">
        {cards.map((c) => (
          <div className={`verdict ${c.cls}`} key={c.art}>
            <span className="v-art">{c.art}</span>
            <strong>{c.head}</strong>
            <p>{c.body}</p>
            {c.ask && (
              <button className="linkbtn" onClick={() => onJump(c.ask!)}>Mergeți la profil →</button>
            )}
          </div>
        ))}
      </div>

      <div className="seg-tabs filter" role="tablist" aria-label="Filtru">
        {([["owed", "De făcut"], ["unknown", "Nedecise"], ["exempt", "Nu se aplică"]] as const).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={filter === id}
            className={filter === id ? "on" : undefined}
            onClick={() => setFilter(id)}
          >
            {label} <span className="cnt">{lists[id].length}</span>
          </button>
        ))}
      </div>

      {filter === "unknown" && lists.unknown.length > 0 && (
        <p className="filter-note">
          Pentru acestea lipsesc răspunsuri. Nu le trecem la „nu se aplică” — ar
          fi o afirmație despre situația dumneavoastră pe care nu avem pe ce să o
          sprijinim.
        </p>
      )}
      {filter === "owed" && lists.owed.length === 0 && (
        <p className="filter-note">Nimic încă — răspundeți în profil ca să vedem ce se aplică.</p>
      )}

      <div className="obs">
        {lists[filter].map((o) => (
          <ObligationRow
            key={o.code}
            item={o}
            kind={filter}
            fresh={fresh.includes(o.code)}
            open={open === o.code}
            onToggle={() => setOpen(open === o.code ? null : o.code)}
            busy={busy === o.code}
            onPatch={onPatch}
            onJump={onJump}
          />
        ))}
      </div>
    </>
  );
}

function ObligationRow({
  item, kind, fresh, open, onToggle, busy, onPatch, onJump,
}: {
  item: Obligation;
  kind: "owed" | "unknown" | "exempt";
  fresh: boolean;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  onPatch: (code: string, body: { state?: string; evidence_ref?: string; determination?: string }) => void;
  onJump: (field: string) => void;
}) {
  const isDetermination = item.kind === "determination";
  const cls = ["ob", kind === "unknown" ? "unknown" : kind === "exempt" ? "exempt" : "", item.needs_review_since ? "flagged" : ""]
    .filter(Boolean).join(" ");

  return (
    <div className={cls}>
      <button className="ob-head" aria-expanded={open} onClick={onToggle}>
        <span className="ob-title">{item.title}</span>
        {fresh && <span className="new">nou</span>}
        {item.kind === "event" && <span className="tag">la incident</span>}
        {isDetermination && <span className="tag">concluzie</span>}
        {kind === "owed" && item.state !== "open" && (
          <span className={`state ${item.state}`}>{STATE_LABEL[item.state]}</span>
        )}
        <span className="art">{item.article}</span>
      </button>

      {item.needs_review_since && (
        // The monitoring product's one visual promise: the article moved
        // under an entry the customer had already dealt with.
        <p className="review-flag">Articolul citat s-a modificat. {item.review_reason}</p>
      )}

      {item.missing_answers.length > 0 && (
        <div className="missing">
          <span>Răspundeți la:</span>
          {item.missing_answers.map((f) => (
            <button key={f} className="miss" onClick={() => onJump(f)}>
              {FIELD_LABEL[f] ?? f}
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="ob-body">
          <p>{item.summary}</p>
          {item.evidence_expected && (
            <p><strong>Dovada:</strong> {item.evidence_expected}</p>
          )}
          {item.fine_tier && (
            <p className="fine">
              Amendă: {FINE_LABEL[item.fine_tier]}. Art. 90 alin. (4) aplică gradual
              10% în primul an, 40% în al doilea, 100% din al treilea, și doar pentru
              încălcări intenționate sau din neglijență (art. 87 alin. (3)).
            </p>
          )}
          <a href={articleUrl(item.family_id, item.path)} target="_blank" rel="noreferrer">
            Citiți {item.article} în Legea 195/2024
          </a>

          {kind === "owed" && (
            <div className="ob-work">
              {isDetermination && (
                <label className="ob-field">
                  Concluzia dumneavoastră, pe scurt — aceasta arătați dacă vi se cere:
                  <textarea
                    className="input"
                    rows={3}
                    defaultValue={item.determination}
                    disabled={busy}
                    onBlur={(e) =>
                      e.target.value !== item.determination &&
                      onPatch(item.code, { determination: e.target.value })
                    }
                  />
                </label>
              )}
              <label className="ob-field">
                Unde se găsește dovada (document, link, o propoziție):
                <input
                  className="input"
                  type="text"
                  defaultValue={item.evidence_ref}
                  disabled={busy}
                  onBlur={(e) =>
                    e.target.value !== item.evidence_ref &&
                    onPatch(item.code, { evidence_ref: e.target.value })
                  }
                />
              </label>
              <div className="pills" role="group" aria-label="Stare">
                {(["open", "in_progress", "done", "not_applicable"] as const).map((s) => (
                  <button
                    key={s}
                    disabled={busy}
                    className={item.state === s ? "pill on" : "pill"}
                    aria-pressed={item.state === s}
                    onClick={() => onPatch(item.code, { state: s })}
                  >
                    {STATE_LABEL[s]}
                  </button>
                ))}
              </div>
              {item.state === "not_applicable" && (
                <p className="fine">
                  „Nu se aplică” este concluzia dumneavoastră, consemnată ca atare.
                  Scrieți motivul mai sus — fără el, consemnarea nu ajută la nimic.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- register

const EMPTY_ACTIVITY: Partial<Activity> = {
  name: "", purpose: "", lawful_basis_art: "", subject_categories: [],
  data_categories: [], recipient_categories: [], transfers_to: [],
  retention_rule: "", security_measures: "", confirmed: false,
};

const ACTIVITY_FIELDS: { key: keyof Activity; label: string; hint?: string; long?: boolean }[] = [
  { key: "purpose", label: "Scopul", long: true },
  { key: "lawful_basis_art", label: "Temeiul legal", hint: "ex. art. 6 alin. (1) lit. b) — executarea contractului" },
  { key: "subject_categories", label: "Persoane", hint: "categorii, nu nume: Angajați, Clienți, Candidați" },
  { key: "data_categories", label: "Date", hint: "Date de identificare, IDNP, Date de contact" },
  { key: "recipient_categories", label: "Destinatari", hint: "Contabilitate, Banca, Curierat" },
  { key: "transfers_to", label: "Transferuri în afara RM", hint: "statul sau organizația, dacă există" },
  { key: "retention_rule", label: "Păstrare", long: true },
  { key: "security_measures", label: "Securitate", hint: "descriere generală (art. 32)", long: true },
];

function show(value: unknown): string {
  return Array.isArray(value) ? value.join(", ") : String(value ?? "");
}

function Register({
  activities, gaps, contacts, busy, onSave, onDelete, onAdd, onSaveContact, onError,
}: {
  activities: Activity[];
  gaps: ActivityGap[];
  contacts: Contact[];
  busy: string | null;
  onSave: (id: string, body: Partial<Activity>) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
  onSaveContact: (body: Omit<Contact, "id">) => void;
  onError: (m: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const gapBy = new Map(gaps.map((g) => [g.id, g]));

  return (
    <>
      <div className="reg-head">
        <p>
          Evidența cerută de art. 30. Propunerile vin din profil — confirmați-le
          pe rând. Se pune la dispoziția Centrului la cerere (alin. (4)).
        </p>
        <button className="btn" disabled={busy === "new"} onClick={onAdd}>+ Activitate</button>
        <button
          className="btn primary"
          disabled={exporting}
          onClick={async () => {
            setExporting(true);
            try {
              await api.exportRegister();
            } catch (e) {
              onError(e instanceof Error ? e.message : "Exportul a eșuat.");
            } finally {
              setExporting(false);
            }
          }}
        >
          {exporting ? "Se pregătește…" : "Export pentru Centru"}
        </button>
      </div>

      <div className="acts">
        {activities.length === 0 && (
          <p className="filter-note">Nicio activitate încă. Răspunsurile din profil propun primele rânduri.</p>
        )}
        {activities.map((a) => (
          <ActivityRow
            key={a.id}
            row={a}
            gap={gapBy.get(a.id)}
            open={open === a.id}
            onToggle={() => setOpen(open === a.id ? null : a.id)}
            busy={busy === a.id}
            onSave={onSave}
            onDelete={onDelete}
          />
        ))}
      </div>

      <Contacts contacts={contacts} onSave={onSaveContact} />
    </>
  );
}

function ActivityRow({
  row, gap, open, onToggle, busy, onSave, onDelete,
}: {
  row: Activity;
  gap?: ActivityGap;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  onSave: (id: string, body: Partial<Activity>) => void;
  onDelete: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Activity>(row);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => setDraft(row), [row]);

  const missing = new Map((gap?.missing ?? []).map((m) => [m.field, m]));
  const required = (gap?.missing ?? []).filter((m) => m.required).length;
  const missingCount = gap?.missing.length ?? 0;

  return (
    // A pre-filled row is visibly a suggestion until the customer has
    // looked at it: shown as finished, the register would make a claim
    // they never made.
    <div className={row.confirmed ? "act" : "act sug"}>
      <button className="act-head" aria-expanded={open} onClick={onToggle}>
        <span className="nm">
          <strong>{row.name || "Activitate fără nume"}</strong>
          <span>{row.lawful_basis_art || "fără temei indicat"}</span>
        </span>
        {missingCount > 0 && (
          <span className="st warn" title={required ? `${required} obligatorii` : "dacă e posibil"}>
            {missingCount} lipsă
          </span>
        )}
        <span className={row.confirmed ? "st ok" : "st sug"}>{row.confirmed ? "confirmat" : "propunere"}</span>
      </button>

      {open && !editing && (
        <div className="act-body">
          <dl>
            {ACTIVITY_FIELDS.map((f) => {
              const m = missing.get(f.key);
              const v = show(row[f.key]);
              return (
                <div className="dl-row" key={f.key}>
                  <dt>{f.label}</dt>
                  <dd className={m ? "gap" : undefined}>
                    {v || (m ? (m.required ? `de completat · ${m.article}` : "de completat, dacă e posibil") : "—")}
                  </dd>
                </div>
              );
            })}
          </dl>
          <div className="act-actions">
            {!row.confirmed && (
              <button className="btn primary" disabled={busy} onClick={() => onSave(row.id, { ...row, confirmed: true })}>
                Confirmă
              </button>
            )}
            <button className="btn" onClick={() => setEditing(true)}>Editează</button>
          </div>
        </div>
      )}

      {open && editing && (
        <div className="act-body">
          <label className="ob-field">
            Denumirea activității
            <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          {ACTIVITY_FIELDS.map((f) => {
            const value = draft[f.key];
            const list = Array.isArray(value);
            const m = missing.get(f.key);
            return (
              <label className="ob-field" key={f.key}>
                <span>
                  {f.label}
                  {m && <span className="fine"> · {m.required ? "obligatoriu" : "dacă e posibil"} · {m.article}</span>}
                  {f.hint && <span className="fine"> · {f.hint}</span>}
                </span>
                {f.long ? (
                  <textarea
                    className="input"
                    rows={2}
                    value={show(value)}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value } as Activity)}
                  />
                ) : (
                  <input
                    className="input"
                    value={show(value)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        [f.key]: list
                          ? e.target.value.split(",").map((v) => v.trim()).filter(Boolean)
                          : e.target.value,
                      } as Activity)
                    }
                  />
                )}
              </label>
            );
          })}
          <p className="fine">
            Nu scrieți aici nume de clienți sau de angajați. Registrul cere
            categorii, nu persoane.
          </p>
          <div className="act-actions">
            <button
              className="btn primary"
              disabled={busy}
              // Editing is what turns a suggestion into a record: saving
              // confirms, so rows do not stay perpetually unconfirmed.
              onClick={() => {
                onSave(row.id, { ...draft, confirmed: true });
                setEditing(false);
              }}
            >
              Salvează
            </button>
            <button className="btn" onClick={() => { setDraft(row); setEditing(false); }}>Renunță</button>
            {confirmDelete ? (
              <button className="btn danger ml" onClick={() => onDelete(row.id)}>Ștergeți?</button>
            ) : (
              <button className="btn ml" onClick={() => setConfirmDelete(true)}>Șterge</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Contacts({
  contacts, onSave,
}: {
  contacts: Contact[];
  onSave: (body: Omit<Contact, "id">) => void;
}) {
  const roles: Contact["role"][] = ["operator", "operator_asociat", "reprezentant", "dpo"];
  return (
    <div className="contacts">
      <h3>Persoane de contact</h3>
      <p className="muted">
        Art. 30 alin. (1) lit. a) cere numele și datele de contact ale
        operatorului, ale reprezentantului și ale responsabilului cu protecția
        datelor. Singurul loc din serviciu în care se păstrează date despre
        persoane, și doar atât cât cere articolul.
      </p>
      {roles.map((role) => (
        <ContactRow key={role} role={role} contact={contacts.find((c) => c.role === role)} onSave={onSave} />
      ))}
    </div>
  );
}

function ContactRow({
  role, contact, onSave,
}: {
  role: Contact["role"];
  contact?: Contact;
  onSave: (body: Omit<Contact, "id">) => void;
}) {
  const [name, setName] = useState(contact?.name ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  useEffect(() => {
    setName(contact?.name ?? "");
    setEmail(contact?.email ?? "");
    setPhone(contact?.phone ?? "");
  }, [contact]);
  const dirty = name !== (contact?.name ?? "") || email !== (contact?.email ?? "") || phone !== (contact?.phone ?? "");

  return (
    <div className="contact">
      <span className="contact-role">{ROLE_LABEL[role]}</span>
      <input className="input" aria-label={`${ROLE_LABEL[role]}: nume`} placeholder="nume" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="input" aria-label={`${ROLE_LABEL[role]}: email`} type="email" placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input className="input" aria-label={`${ROLE_LABEL[role]}: telefon`} placeholder="telefon" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <button className="btn medium" disabled={!dirty} onClick={() => onSave({ role, name, email, phone })}>
        Salvează
      </button>
    </div>
  );
}
